import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import type {
  LineGraphData,
  StreamType,
} from '~/components/LineGraph/LineGraph';
import { PerformanceDeltaLabel } from '~/modules/artist-roster/components/TrackList/PerformanceDeltaLabel';

interface TrackStreamsGraphTooltipProps {
  data?: LineGraphData[];
  lineColors?: string[];
  index?: number;
  streamType?: StreamType;
}

function getLineDataSection(
  labelText: string,
  lineIndex: number,
  dataIndex: number,
  wowLabel: string,
  data?: LineGraphData[],
  lineColors?: string[],
) {
  return (
    <>
      <div className="flex items-center">
        <div
          style={{
            fontSize: 6,
          }}
          className="pr-xs font-medium"
        >
          {labelText}
        </div>
        <div
          style={{
            fontSize: 10,
            color: lineColors?.[lineIndex],
          }}
          className="font-extrabold"
        >
          ·
        </div>
      </div>
      <div style={{ fontSize: 8 }} className="inline-flex">
        {data?.[lineIndex]?.[dataIndex].y.toLocaleString() ?? '--'}{' '}
        {data?.[lineIndex]?.[dataIndex].delta ? (
          <PerformanceDeltaLabel
            className="ml-xs text-[.35rem]"
            delta={data[lineIndex][dataIndex].delta}
            dateRange={wowLabel}
          />
        ) : null}
      </div>
    </>
  );
}

export function TrackStreamsGraphTooltip({
  data,
  streamType,
  lineColors,
  index,
}: TrackStreamsGraphTooltipProps) {
  const { t } = useTranslation('roster');
  const firstLabelText =
    streamType === 'streams'
      ? t('trackDetails.labels.streamsRTD')
      : t('trackDetails.labels.library');

  const dataIndex = index || 0;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: '#232323',
          minWidth: 5,
          minHeight: 5,
          rotate: '45deg',
          clipPath: 'polygon(0 0,100% 100%,0 100%)',
          border: '0.75px solid #FFFFFF1F',
          marginRight: -3,
          zIndex: 10,
        }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 4,
          width: 'fit-content',
          backgroundColor: '#232323',
          color: 'white',
          border: '0.75px solid #FFFFFF1F',
          borderRadius: 2,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '1px 3px',
            fontSize: 6,
            backgroundColor: '#FFFFFF0F',
            borderRadius: 1,
          }}
        >
          <Icon type="calendar-day" size="xs" />
          <div style={{ textWrap: 'nowrap' }}>
            {data?.[0]?.[dataIndex].date
              ? format(parseISO(data[0][dataIndex].date ?? ''), 'MMM d, yyyy')
              : ''}
            <span className="text-subtle">{` (${t('trackDetails.labels.week')} ${data?.[0]?.[dataIndex].x})`}</span>
          </div>
        </div>
        {getLineDataSection(
          firstLabelText,
          0,
          dataIndex,
          t('trackDetails.labels.wow'),
          data,
          lineColors,
        )}
        {streamType === 'organicStreams'
          ? getLineDataSection(
              t('trackDetails.labels.search'),
              1,
              dataIndex,
              t('trackDetails.labels.wow'),
              data,
              lineColors,
            )
          : null}
      </div>
    </div>
  );
}

TrackStreamsGraphTooltip.displayName = 'TrackStreamsGraphTooltip';
